import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAccount } from "@/entities/accounts/hooks";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import ProductDrawer from "../components/ProductDrawer";
import { useCreateProduct, useDeleteProduct, useUpdateProduct } from "../hooks";
import { ProductFormData, ProductV2 } from "../types";

const TRANSLATION_KEY = "settings.product";

interface UseProductEditorOptions {
  /** All of the account's products; the last one can't be deleted. */
  products: ProductV2[];
  refetchProducts: () => void;
  onDeleted?: (productId: string) => void;
}

export const useProductEditor = ({
  products,
  refetchProducts,
  onDeleted
}: UseProductEditorOptions) => {
  const { t } = useTranslation();
  const { data: accountData } = useAccount();
  const createProductMutation = useCreateProduct();
  const updateProductMutation = useUpdateProduct();
  const deleteProductMutation = useDeleteProduct();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ProductV2>();
  const [pendingFormData, setPendingFormData] =
    useState<ProductFormData | null>(null);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

  const openAddProduct = () => {
    setEditingProduct(undefined);
    setIsDrawerOpen(true);
  };

  const openEditProduct = (product: ProductV2) => {
    setEditingProduct(product);
    setIsDrawerOpen(true);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setEditingProduct(undefined);
  };

  const handleSave = (formData: ProductFormData) => {
    if (editingProduct) {
      setPendingFormData(formData);
      setIsUpdateModalOpen(true);
      return;
    }
    createProductMutation.mutate(
      { data: formData },
      {
        onSuccess: () => {
          refetchProducts();
          closeDrawer();
        }
      }
    );
  };

  const resetUpdateModal = () => {
    setIsUpdateModalOpen(false);
    setPendingFormData(null);
  };

  const handleConfirmUpdate = () => {
    if (!editingProduct || !pendingFormData) return;
    updateProductMutation.mutate(
      {
        pathParams: { productId: editingProduct.id },
        data: pendingFormData
      },
      {
        onSuccess: () => {
          refetchProducts();
          closeDrawer();
        }
      }
    );
    resetUpdateModal();
  };

  const handleDelete = (productId: string) => {
    deleteProductMutation.mutate(
      { pathParams: { productId } },
      {
        onSuccess: () => {
          refetchProducts();
          onDeleted?.(productId);
        }
      }
    );
  };

  const productIndex = editingProduct
    ? Math.max(
        0,
        products.findIndex((product) => product.id === editingProduct.id)
      )
    : products.length;

  const dialogs = (
    <>
      <ProductDrawer
        isOpen={isDrawerOpen}
        product={editingProduct}
        index={productIndex}
        onClose={closeDrawer}
        onSave={handleSave}
        onDelete={handleDelete}
        accountName={accountData?.name}
        totalProductCount={products.length}
      />
      <ConfirmationModal
        isOpen={isUpdateModalOpen}
        setIsOpen={setIsUpdateModalOpen}
        title={t(`${TRANSLATION_KEY}.updateProductModal.title`)}
        confirmationText={
          <div className="flex flex-col gap-4">
            <div>
              <div>
                {t(`${TRANSLATION_KEY}.updateProductModal.description`)}
              </div>
              <div>
                {t(`${TRANSLATION_KEY}.updateProductModal.descriptionLine2`)}
              </div>
            </div>
            {t(`${TRANSLATION_KEY}.updateProductModal.descriptionLine3`)}
          </div>
        }
        handleSubmit={handleConfirmUpdate}
        handleCancel={resetUpdateModal}
        isPrimaryButton={true}
        submitButtonText={t(
          `${TRANSLATION_KEY}.updateProductModal.submitButton`
        )}
        data-test="products-update-product-modal"
      />
    </>
  );

  return { openAddProduct, openEditProduct, dialogs };
};
